import { ImageName } from "@mcleod/images";
import {
    Button, ButtonProps, ButtonVariant, Component, HorizontalSpacer,
    Image, ImageProps, Layout, Panel, PanelProps, TabStyle
} from "@mcleod/components";
import { Alignment, CompanySettings, getThemeColor, HorizontalAlignment } from "@mcleod/core";
import { AppTabset } from "../tabset/AppTabset";
import { AppTab } from "../tabset/AppTab";

export interface AppHeaderActionPanels {
    layout: Layout;
    addlPanelLeft: Panel;
    addlPanelRight: Panel;
    panelTools?: Panel;
    // TODO handle context menu actions defined in panel tools' ellipsis.
}

export class AppHeaderActions extends Panel {
    private _contextButton: Button;
    private header: AppHeaderActionPanels;
    private _appSpecificActionPanel: Panel;

    constructor(header: AppHeaderActionPanels) {
        super({
            height: "100%",
            minWidth: "85%",
            fillRow: true,
            align: HorizontalAlignment.RIGHT,
            backgroundColor: "defaultBackground",
            scrollY: true
        });
        this.header = header;
        this.addClickListener(() => this.hideContextActions());
    }

    private get appSpecificActionPanel(): Panel {
        if (this._appSpecificActionPanel == null) {
            this._appSpecificActionPanel = new Panel({
                id: "appSpecificActionPanel",
                fillRow: true,
                rowBreak: true,
                padding: 0,
                margin: 0
            });
        }
        return this._appSpecificActionPanel;
    }

    public addAppAction(button: Button): void {
        this.appSpecificActionPanel.add(button);
    }

    public get contextButton(): Button {
        if (this._contextButton == null) {
            this._contextButton = new Button({
                id: "btnHeaderActionMenu",
                backgroundColor: "transparent",
                variant: ButtonVariant.round,
                imageWidth: 20,
                imageHeight: 20,
                imageName: ImageName.ellipsis,
                imageColor: CompanySettings.get().text_color ?? getThemeColor("primary.reverse"),
                imageRotation: 90,
                caption: null,
                width: null,
                height: null,
                padding: 0,
                paddingTop: 4,
                margin: 0,
                onClick: () => { this.onContextButtonClick(); }
            });
        }
        return this._contextButton;
    }

    private onContextButtonClick(): void {
        this.syncActions();
        this.displayContextActions();
    }

    private displayContextActions() {
        this.slideIn({
            ...transitionOptions,
            coverPageHeader: false
        }, true, {
            closeOnClickOff: true,
            greyedBackground: true,
            coverPageHeader: true,
            onClose: () => this.hideContextActions()
        });
    }

    private hideContextActions() {
        this.slideOut(transitionOptions);
    }

    private get actionPanels(): Panel[] {
        const result: Panel[] = [this.header.addlPanelLeft, this.appSpecificActionPanel, this.header.addlPanelRight];
        const tabPanels = this.tabSectionHeadingPanels;
        if (tabPanels != null && tabPanels.length > 0) {
            result.push(...this.tabSectionHeadingPanels);
        }
        if (this.panelTools != null) {
            result.push(this.panelTools);
        }
        return result;
    }

    private get tabSectionHeadingPanels(): Panel[] | null {
        const layout = this.header.layout;
        if (layout == null) return null;
        const tabset = layout.findComponentByType(AppTabset);
        if (tabset == null || tabset.originalTabStyle !== TabStyle.SCROLL) return null;

        const result: Panel[] = [];
        const tabs: AppTab[] = layout.findComponentsByType(AppTab);
        tabs.forEach((tab: AppTab) => {
           const panel = tab["_addlTitleCompsPanel"];
           if (panel == null || panel.components == null || panel.components.length == 0)
               return;
           else
               result.push(panel);
        });
        return result;
    }

    private get panelTools(): Panel | null {
        return this.header.panelTools ?? null;
    }

    public syncActions() {
        this.removeAll();
        for (const panel of this.actionPanels) {
            this.addAppActionSubPanel(panel);
        }
    }

    public get hasActions(): boolean {
        return this.actionPanels?.some((panel: Panel) => this.hasHeaderActions(panel));
    }

    private hasHeaderActions(panel: Panel): boolean {
        return panel?.components?.some((value: Component) => {
            return value.visible;
        });
    }

    private addAppActionSubPanel(panel: Panel, showSeparator: boolean = false) {
        if (this.hasHeaderActions(panel)) {
            panel.components.forEach((c: Component) => {
                this.applyAppHeaderActionComponentProps(c, panel);
            });
            panel.setProps(menuSubPanelProps);
            this.add(panel);
            if (showSeparator)
                this.add(new HorizontalSpacer({
                    marginLeft: 8, marginRight: 8, borderBottomWidth: 1, borderBottomColor: "default.lightest"
                }));
        }
    }

    private applyAppHeaderActionComponentProps(c: Component, originPanel: Panel) {
        if (c instanceof Panel) {
            c.components.forEach((comp: Component) => {
                this.applyAppHeaderActionComponentProps(comp, c);
            });
            c.setProps(menuSubPanelProps);
        } else if (c instanceof Button) {
            c.setProps({
                caption: c.caption ?? c.displayLabel,
                imageName: c.imageName,
                ...menuButtonProps,
                imageProps: {
                    ...c.imageProps,
                    ...menuButtonImageProps,
                },
            });
            c.addClickListener(() => this.hideContextActions(), true);
        }
        else if (c instanceof Image) {
            const convertedComponent = new Button();
            convertedComponent.setProps({
               caption: c.displayLabel,
               ...menuButtonProps,
               imageProps: {
                   name: c.name,
                   width: c.width,
                   height: c.height,
                   color: c.color,
                   rotation: c.rotation,
                   ...menuButtonImageProps,
               }
            });
            originPanel.replace(c, convertedComponent);
        } else {
            c.setProps(menuSubPanelProps);
        }
    }
}

const menuButtonProps: Partial<ButtonProps> = {
    variant: ButtonVariant.text,
    paddingTop: null,
    paddingBottom: null,
    paddingLeft: null,
    paddingRight: null,
    padding: 20,
    fontSize: "large",
    marginTop: null,
    marginBottom: null,
    marginRight: null,
    marginLeft: null,
    align: HorizontalAlignment.RIGHT,
    imageAlign: Alignment.RIGHT,
    fillRow: true,
    rowBreak: true,
    width: null,
    minWidth: null,
    maxWidth: null,
    height: null,
    minHeight: null,
    maxHeight: null,
    color: "default"
}

const menuButtonImageProps: Partial<ImageProps> = {
    color: "primary",
    marginLeft: 12,
    marginRight: null,
    marginTop: null,
    marginBottom: null,
    width: 32,
    height: 32,
    paddingTop: null,
    paddingBottom: null,
    paddingLeft: null,
    paddingRight: null,
    padding: 0
}

const menuSubPanelProps: Partial<PanelProps> = {
    padding: 0,
    margin: 0,
    width: "100%",
    rowBreak: true,
    align: HorizontalAlignment.RIGHT
}

const transitionOptions = {
    speed: 200,
    direction: Alignment.RIGHT
}
